import React, { useState, useRef, useEffect } from 'react'
import FlipMove from 'react-flip-move'
import uuid from 'uuidv4'

import Button from '../../Button/Button'

import { DropzoneProps } from './Dropzone.types'

import useInput from '../useInput'
import Column from '../../Column/Column'
import warning from 'tiny-warning'
import InputWrapper from '../InputWrapper/InputWrapper'
const Dropzone: React.FC<DropzoneProps> = ({
    multiple = false,
    fileTypes,
    noFileMessage = 'No file',
    maxFileSize,
    maxFileSetSize,
    isDisabled,
    isDiscreet = false,
    rejectSpaceInFilename = false,
    ...restProps
}) => {
    const context = useInput({ ...restProps }, 'any')
    const { value, onChange } = context
    const [isDragOver, setIsDragOver] = useState(false)
    const [error, setError] = useState(null)
    const [totalFileSize, setTotalFileSize] = useState(0)
    const fileInputRef = useRef(null)

    useEffect(() => {
        if (value !== undefined && !Array.isArray(value)) {
            throw Error(
                'Dropzone value must by an array. Type of value received was ' +
                    typeof value +
                    '. Value: ' +
                    JSON.stringify(value),
            )
        }

        const files = value || []
        files.forEach((file) => {
            warning(file.fileId !== undefined, 'Dropzone: file ' + file.name + ' is missing a file id!')
        })

        setCurrentFileSetSize()
    }, [value])

    const setCurrentFileSetSize = () => {
        const files = value || []
        let totalFileSize = 0

        files.forEach((file) => {
            totalFileSize += file.size
        })

        setTotalFileSize(totalFileSize)
    }

    const fileListToArray = (list) => {
        const array = []
        for (var i = 0; i < list.length; i++) {
            const item = list.item(i)
            item.fileId = uuid()
            array.push(list.item(i))
        }
        return array
    }

    const validateFiles = (files) => {
        const validFiles = []
        let _totalFileSize = totalFileSize

        files.forEach((file) => {
            const extension = file.name.split('.').pop()
            if (fileTypes) {
                const found = fileTypes.find((x) => x.indexOf(extension) !== -1)

                if (!found) {
                    addTemporaryError('File type ' + extension + ' is not supported')
                    return
                }
            }

            _totalFileSize += file.size

            if (maxFileSize && file.size !== undefined) {
                const maxBytes = maxFileSize * 1000000
                if (file.size > maxBytes) {
                    addTemporaryError('File size is too large. Max size per file is: ' + maxFileSize + 'mb')
                    return
                }
            }

            if (maxFileSetSize) {
                const maxFileSetBytes = maxFileSetSize * 1000000
                if (_totalFileSize > maxFileSetBytes) {
                    addTemporaryError('Maximum file set size reached')
                    return
                }
            }
            if (rejectSpaceInFilename && file.name.indexOf(' ') !== -1) {
                addTemporaryError(
                    'The file name contains a space which is not allowed, please fix the file name and try again',
                )
                return
            }

            validFiles.push(file)
        })

        return validFiles
    }

    const addTemporaryError = (message) => {
        setError(message)
        setTimeout(() => {
            setError(null)
        }, 2000)
    }

    const openFileDialog = (e) => {
        if (isDisabled) {
            return
        }
        if (fileInputRef.current) {
            fileInputRef.current.click()
        }
    }

    const onFilesAdded = (e) => {
        const newFiles = e.target.files || e.dataTransfer.files
        const newFilesArray = fileListToArray(newFiles)
        let validFiles = validateFiles(newFilesArray)
        const existingFiles = Array.isArray(value) && multiple ? value : []

        if (!multiple && validFiles.length > 1) {
            validFiles = [validFiles[validFiles.length - 1]]
        }
        onChange(existingFiles.concat(validFiles))
    }

    const onDragOver = (e) => {
        if (isDisabled) {
            return
        }
        e.preventDefault()
        setIsDragOver(true)
    }
    const onDragLeave = (e) => {
        if (isDisabled) {
            return
        }
        e.preventDefault()
        setIsDragOver(false)
    }
    const onDrop = (e) => {
        if (isDisabled) {
            return
        }
        e.preventDefault()
        onFilesAdded(e)
        setIsDragOver(false)
    }

    const removeFile = (fileId) => {
        if (isDisabled) {
            return
        }
        onChange(value.filter((file) => file.fileId !== fileId))
    }

    const renderFileTypeMessage = () => {
        if (fileTypes && fileTypes.length > 0) {
            return (
                <div>
                    Supported file types:{' '}
                    {fileTypes.map((f, i) => {
                        const del = i !== 0 ? ', ' : ''
                        return (
                            <React.Fragment>
                                {del} <span key={i}>{f}</span>
                            </React.Fragment>
                        )
                    })}
                </div>
            )
        }
        return null
    }

    const formatSizeUnits = (bytes) => {
        if (bytes >= 1073741824) {
            bytes = (bytes / 1073741824).toFixed(2) + ' GB'
        } else if (bytes >= 1048576) {
            bytes = (bytes / 1048576).toFixed(2) + ' MB'
        } else if (bytes >= 1024) {
            bytes = (bytes / 1024).toFixed(2) + ' KB'
        } else if (bytes > 1) {
            bytes = bytes + ' bytes'
        } else if (bytes == 1) {
            bytes = bytes + ' byte'
        } else {
            bytes = '0 bytes'
        }
        return bytes
    }

    const renderFiles = () => {
        if (!Array.isArray(value) || value.length === 0) {
            if (isDiscreet) {
                return <Button size={'xs'} label={'Select File'} />
            }
            return (
                <div className='r-dropzone-no-files'>
                    <div>{noFileMessage}</div>
                    <div>Click or Drag and Drop File{multiple ? 's' : ''}</div>
                    <div>{renderFileTypeMessage()}</div>
                </div>
            )
        }
        return value.map((file, i) => {
            return (
                <div key={file.fileId} className='r-dropzone-file-item'>
                    <Column flexRow noPadding>
                        <div>{file.name}</div>
                        {file.size !== undefined && !isDiscreet ? (
                            <div style={{ fontWeight: 400, marginLeft: '10px' }}>{formatSizeUnits(file.size)}</div>
                        ) : (
                            ''
                        )}
                    </Column>
                    <div>
                        <Button
                            isDiscreet
                            size={isDiscreet ? 'xs' : 'sm'}
                            type='danger'
                            icon={'window-close'}
                            onClick={(e) => {
                                if (isDisabled) {
                                    return
                                }
                                e.preventDefault()
                                e.stopPropagation()
                                removeFile(file.fileId)
                            }}
                        />
                    </div>
                </div>
            )
        })
    }

    return (
        <InputWrapper {...context}>
            <div
                data-testid='Dropzone'
                className={`r-dropzone-container ${isDiscreet ? 'r-display-discreet' : ''} ${
                    isDisabled ? 'r-state-disabled' : ''
                }${isDragOver ? 'r-state-drag-over' : ''}`}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onClick={openFileDialog}
            >
                <input
                    ref={fileInputRef}
                    className='r-file-input'
                    type='file'
                    multiple={multiple}
                    onChange={onFilesAdded}
                />
                <div className='r-dropzone-file-list'>{renderFiles()}</div>
                {error ? <div className={'r-dropzone-errors'}>{error}</div> : ''}

                {!isDiscreet ? (
                    <div>
                        Total Size: {formatSizeUnits(totalFileSize)}
                        {maxFileSetSize ? <span> | Max: {maxFileSetSize} mb</span> : ''}
                    </div>
                ) : null}
            </div>
        </InputWrapper>
    )
}

export default Dropzone
